import Cookies from "js-cookie";
import {
  login,
  LoginInfo,
  refreshTokenData,
  loginByBBLToken,
} from "@/app/api/auth";
import {
  requestUserTokenFromApp,
  requireTicket,
  confirmTicket,
} from "@/app/utils/handyBridge";
import { CONNECT_PATH } from "../constants";
import { tryParse } from "../utils/object";

const PORTAL_LOGIN_URL = `${process.env.NEXT_PUBLIC_PORTAL_URL}/sign-in`;
const LOGIN_PATH = "/";
export const TOKEN_KEY = "bbl_im_token";
export const LOGIN_INFO = "login_info";
const getPath = process.env.NODE_ENV !== "development" ? CONNECT_PATH : "";

interface AuthLoginInfo extends LoginInfo {
  saveAuthTime: number;
}

// 认证事件类型
export enum AuthEventType {
  LOGOUT = "logout",
  LOGIN = "login",
  TOKEN_EXPIRED = "token_expired",
}

/**
 * 通过handy app的token去获取ticket
 * 然后通过ticket去我们自己的接口获取用户信息和token
 * @returns
 */
export const getHandyAppLogin = async () => {
  try {
    // 获取用户Token
    const { accessToken } = await requestUserTokenFromApp({ shouldLogin: 1 });
    // const accessToken =
    //   "AACz_2KGV453SOVlWS2R2L4g5v_evytRdROrHFG799yBB2oP9ARowwP9mRwWqDhRRcg2wQIuYNk-aT6aLI6mSxFtTVPT7_eOldcXp0MyYqyUlx6TwvovxL-p0oeoK-W54sSA3zzp0R0-0rrT";
    // const { ticket } = await requireTicket(accessToken as string);
    // confirmTicket(ticket as string, accessToken as string);
    const res = await loginByBBLToken(accessToken);
    // const res = await login(ticket);4
    authService.setToken(res.accessToken, res);
    return res;
  } catch (error) {
    console.error("Failed to get login:", error);
    throw error;
  }
};

// 事件监听器类型
type AuthEventListener = () => void;

class AuthService {
  private static instance: AuthService;
  private loginToken: string | null = null; // 用户登录时的初始token
  private eventListeners: Map<AuthEventType, Set<AuthEventListener>> =
    new Map();

  private static get FULL_LOGIN_URL() {
    return `${window.location.origin}${getPath}`;
  }

  private constructor() {}

  static getInstance() {
    if (!AuthService.instance) {
      this.instance = new AuthService();
    }
    return this.instance;
  }

  /**
   * 添加事件监听器
   */
  on(event: AuthEventType, listener: AuthEventListener) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(listener);

    // 返回取消订阅函数
    return () => this.off(event, listener);
  }

  /**
   * 移除事件监听器
   */
  off(event: AuthEventType, listener: AuthEventListener) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * 触发事件
   */
  private emit(event: AuthEventType) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener();
        } catch (error) {
          console.error(`Error in ${event} listener:`, error);
        }
      });
    }
  }

  setToken(token: string, info?: LoginInfo) {
    // 设置cookie中的token
    Cookies.set(TOKEN_KEY, token, { path: "/" });
    if (!this.loginToken) {
      this.loginToken = token;
    }

    // 如果还没有登录token，则将其设为登录token
    if (info) {
      localStorage.setItem(
        LOGIN_INFO,
        JSON.stringify({
          ...info,
          saveAuthTime: Date.now(),
        }),
      );
    }
  }

  /**
   * 续期token
   * @returns void
   */
  async renewalToken() {
    const loginInfo = tryParse(
      localStorage.getItem(LOGIN_INFO) || "",
    ) as AuthLoginInfo;
    if (loginInfo) {
      const { saveAuthTime, accessTokenExpiresIn, refreshToken } = loginInfo;
      // 当距离过期时间10%时，认为即将过期，更新token
      const threshold = 0.8;
      if (
        (Date.now() - saveAuthTime) / (Number(accessTokenExpiresIn) * 1000) >
        threshold
      ) {
        try {
          const res = await refreshTokenData(refreshToken);
          this.setToken(res.accessToken, res);
        } catch (error) {
          console.log(error);
          // 失败了也更新时间
          this.setToken(loginInfo.accessToken, loginInfo);
        }
      }
    }
  }

  getToken() {
    return Cookies.get(TOKEN_KEY);
  }

  // 获取登录时的原始token
  getLoginToken() {
    return this.loginToken;
  }

  clearToken() {
    // 清理cookie
    Cookies.remove(TOKEN_KEY, { path: "/" });
    // 清理登录信息
    localStorage.removeItem(LOGIN_INFO);
    // 清理登录token
    this.loginToken = null;
  }

  toLogin(search: string, redirect: string) {
    // 官网回调地址，用于登录成功后跳转回当前页面
    const returnUrl = new URL(AuthService.FULL_LOGIN_URL + search);
    if (redirect) {
      returnUrl.searchParams.set("redirect", redirect);
    }

    // 跳转到官网的统一登录页面
    const portalUrl = new URL(PORTAL_LOGIN_URL);
    portalUrl.searchParams.set("ticket", "1");
    portalUrl.searchParams.set("to", returnUrl.toString());
    window.location.replace(portalUrl.toString());
  }

  async login(doNotUseTicket = false, target = "") {
    const href = window.location.href;
    const url = new URL(href);
    if (target) {
      url.searchParams.set("target", target);
    }
    const ticket = url.searchParams.get("ticket");
    const getPath = process.env.NODE_ENV !== "development" ? CONNECT_PATH : "";
    const redirect = url.searchParams.get("redirect") || getPath;
    const search = url.search || "";

    // 如果 ticket 为空，则跳转到官网的统一登录页面
    if (!ticket || doNotUseTicket) {
      this.toLogin(search, redirect);
      return;
    }

    try {
      const res = await login(ticket);
      this.setToken(res.accessToken, res);
      // 如果登录成功，则跳转回当前页面
      window.location.replace(redirect);
    } catch {
      console.log("error");
      this.toLogin(search, redirect);
    }
  }

  logout(reason: "manual" | "token_expired" = "manual") {
    // clearToken会清理cookie和登录token
    this.clearToken();

    // 触发登出事件，通知所有监听器（包括 AuthProvider）
    if (reason === "token_expired") {
      this.emit(AuthEventType.TOKEN_EXPIRED);
    }
    this.emit(AuthEventType.LOGOUT);

    this.redirectToLogin();
  }

  redirectToLogin(needRedirect: boolean = true) {
    // const redirect = `${window.location.pathname}${window.location.search}`;
    const redirectUrl = needRedirect ? `${getPath}${LOGIN_PATH}` : LOGIN_PATH;
    window.location.replace(redirectUrl);
  }

  // 获取用户信息
  async fetchHandyAppLogin() {
    try {
      return await getHandyAppLogin();
    } catch (error) {
      console.error("Error fetching login:", error);
    }
  }
}

export const authService = AuthService.getInstance();
